import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Tooltip,
    Container,
    TextField,
    InputAdornment,
    CircularProgress,
    Chip
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import api from '../services/api';
import { Organization } from '../types/organization';
import OrganizationModal from '../components/organization/OrganizationModal';
import OrganizationDetails from '../components/organization/OrganizationDetails';
import { useToast } from '../context/ToastContext';
import ConfirmDialog from '../components/common/ConfirmDialog';

const OrganizationManagement: React.FC = () => {
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
    const { showToast } = useToast();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [orgToDelete, setOrgToDelete] = useState<string | null>(null);

    const fetchOrganizations = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/organizations');
            setOrganizations(res.data);
        } catch (err) {
            console.error('Error fetching organizations', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrganizations();
    }, []);

    const handleCreate = () => {
        setSelectedOrg(null);
        setModalOpen(true);
    };

    const handleEdit = (org: Organization) => {
        setSelectedOrg(org);
        setModalOpen(true);
    };

    const handleView = (org: Organization) => {
        setSelectedOrg(org);
        setDetailsOpen(true);
    };

    const handleDeleteClick = (orgId: string) => {
        setOrgToDelete(orgId);
        setConfirmOpen(true);
    };

    const confirmDeleteOrg = async () => {
        if (!orgToDelete) return;
        try {
            await api.delete(`/api/organizations/${orgToDelete}`);
            setOrganizations(prev => prev.filter(o => o._id !== orgToDelete));
            showToast('Organisme supprimé avec succès', 'success');
        } catch (err) {
            console.error('Error deleting organization', err);
            showToast('Erreur lors de la suppression', 'error');
        } finally {
            setConfirmOpen(false);
            setOrgToDelete(null);
        }
    };

    const handleSave = (savedOrg: Organization) => {
        if (selectedOrg) {
            setOrganizations(prev => prev.map(o => o._id === savedOrg._id ? savedOrg : o));
        } else {
            setOrganizations(prev => [...prev, savedOrg]);
        }
    };

    const filteredOrgs = organizations.filter(org =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.subdomain.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" mb={4} gap={2}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ mb: { xs: 0, sm: 2 } }}>
                    Gestion des Organismes
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                    fullWidth={false}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Nouvel Organisme
                </Button>
            </Box>

            <Box mb={4}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Rechercher un organisme..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filteredOrgs.map((org) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={org._id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 4
                                    }
                                }}
                            >
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box display="flex" alignItems="center" mb={2}>
                                        {org.logoUrl ? (
                                            <Box
                                                component="img"
                                                src={org.logoUrl}
                                                alt={org.name}
                                                sx={{ width: 40, height: 40, mr: 2, objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    mr: 2,
                                                    bgcolor: org.theme?.primaryColor || 'primary.main',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white'
                                                }}
                                            >
                                                <BusinessIcon />
                                            </Box>
                                        )}
                                        <Typography variant="h6" component="div">
                                            {org.name}
                                        </Typography>
                                    </Box>
                                    <Typography color="text.secondary" gutterBottom>
                                        {org.subdomain}
                                    </Typography>
                                    <Box mt={2}>
                                        <Chip
                                            label={new Date(org.createdAt).toLocaleDateString()}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                </CardContent>
                                <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                                    <Tooltip title="Voir détails et statistiques">
                                        <IconButton size="small" color="info" onClick={() => handleView(org)}>
                                            <ViewIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Modifier">
                                        <IconButton size="small" color="primary" onClick={() => handleEdit(org)}>
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Supprimer">
                                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(org._id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                    {filteredOrgs.length === 0 && (
                        <Grid size={{ xs: 12 }}>
                            <Box textAlign="center" py={4}>
                                <Typography color="text.secondary">
                                    Aucun organisme trouvé
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}

            <OrganizationModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSave}
                organization={selectedOrg}
            />

            <OrganizationDetails
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                organization={selectedOrg}
            />

            <ConfirmDialog
                open={confirmOpen}
                title="Supprimer l'organisme"
                content="Êtes-vous sûr de vouloir supprimer cet organisme ? Cette action est irréversible."
                onConfirm={confirmDeleteOrg}
                onCancel={() => setConfirmOpen(false)}
            />
        </Container>
    );
};

export default OrganizationManagement;
